<template>
  <div class="container mt-5 px-2">
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link :to="'/invoices'">Invoices</router-link></li>
          <li class="is-active"><a href="#" aria-current="page">{{costId}}</a></li>
        </ul>
      </nav>
      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>
      <div class="mt-3">
        <div v-if="waitings.loading">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>
        <div v-else>
          <div v-if="model">

            <nav class="level">
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Created By</p>
                  <p class="title is-6">{{model.createdBy}}</p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Created At</p>
                  <p class="title is-6">{{model.createdAt}}</p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Updated At</p>
                  <p class="title is-6">{{model.updatedAt}}</p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Status</p>
                  <p>
                    <span class="tag is-link is-medium" v-if="model.status == 'Open'">Open</span>
                    <span class="tag is-success is-medium" v-if="model.status == 'Approved'">Approved</span>
                    <span class="tag is-warning is-medium" v-if="model.status == 'Rejected'">Rejected</span>
                    <span class="tag is-dark is-medium" v-if="model.status == 'NS Bill Created'">NS Bill Created</span>
                  </p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">
                    <span v-if="cost.status == 'Open'">Updated By</span>
                    <span v-if="cost.status == 'Approved'">Updated By</span>
                    <span v-if="cost.status == 'Rejected'">Updated By</span>
                    <span v-if="cost.status == 'NS Bill Created'">Updated By</span>
                  </p>
                  <p class="title is-6">{{model.closedBy}}</p>
                </div>
              </div>
            </nav>

            <div class="field">
              <label class="label">Subject</label>
              <div class="control">
                <input class="input" type="text" placeholder="Subject" v-model="model.subject" :readonly="!canEdit">
              </div>
            </div>

            <div class="field">
              <label class="label">Description</label>
              <div class="control">
                <textarea class="textarea" placeholder="Description" v-model="model.description" :readonly="!canEdit"></textarea>
              </div>
            </div>

            <div class="field">
              <label class="label">Attachments</label>
              <div class="control">
                <attachments :attachments="model.attachments" :canEdit="canEdit" :infoRetrieval="'invoice'" @new-attachment-created="addAttachment" @attachment-deleted="removeAttachment" :preview="true" />
              </div>
            </div>

            <div class="field">
              <label class="label">Amount</label>
              <div class="control">
                <input class="input" type="number" v-model.number="model.amount" :readonly="!canEdit">
              </div>
            </div>

            <div class="field">
              <label class="label">Reviewer</label>
              <div class="control">
                <div class="select">
                  <select v-model="model.reviewers" :disabled="!canEdit">
                    <option v-for="(email, i) in emails" :key="'email-option-' + i">
                      {{email}}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="field" v-if="false">
              <label class="label">Tags</label>
              <div class="control" v-if="model.tags.length">
                <div class="input is-static">
                  <span v-for="(t, i) in model.tags" :key="'model-tag-' + i">
                    <span class="tag is-info is-medium">
                      {{t}}&nbsp;
                      <button class="delete is-small" @click="removeTag(i)"></button>
                    </span>&nbsp;
                  </span>
                </div>
              </div>
              <div class="field has-addons" v-if="canEdit">
                <div class="control">
                  <input class="input" type="text" placeholder="New Tag" v-model="newTag">
                </div>
                <div class="control">
                  <a class="button" @click="addTag">
                    Add
                  </a>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">Comment</label>
              <div class="control">
                <textarea class="textarea" placeholder="Comment" v-model="model.comment" :readonly="!canEdit"></textarea>
              </div>
            </div>

            <div class="field">
              <label class="label">Invoice Date <span class="has-text-danger">*</span></label>
              <div class="control">
                <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="model.invoiceDate" :readonly="!canEdit && !accountingEditing">
              </div>
            </div>

            <div class="field">
              <label class="label">Invoice Number <span class="has-text-danger">*</span></label>
              <div class="control">
                <input class="input" type="text" placeholder="Invoice Number" v-model="model.invoiceNumber" :readonly="!canEdit && !accountingEditing">
              </div>
            </div>

            <div class="field">
              <label class="label">Vendor <span class="has-text-danger">*</span></label>
              <div class="field has-addons">
                <div class="control">
                  <span class="select">
                    <select v-model="model.vendorNameMode" :disabled="!canEdit && !accountingEditing">
                      <option>Select</option>
                      <option>New</option>
                    </select>
                  </span>
                </div>
                <div class="control is-expanded model-select-container" v-if="model.vendorNameMode == 'Select'">
                  <model-select :options="vendorOptions" v-model="model.selectedVendorName" :isDisabled="!canEdit && !accountingEditing"/>
                </div>
                <div class="control is-expanded" v-if="model.vendorNameMode == 'New'">
                  <input class="input" type="text" placeholder="Input new vendor" v-model="model.newVendorName" :readonly="!canEdit && !accountingEditing">
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">Subsidiary <span class="has-text-danger">*</span></label>
              <div class="field has-addons">
                <div class="control">
                  <span class="select">
                    <select v-model="model.subsidiaryMode" :disabled="!canEdit && !accountingEditing">
                      <option>Select</option>
                      <option>New</option>
                    </select>
                  </span>
                </div>
                <div class="control is-expanded" v-if="model.subsidiaryMode == 'Select'">
                  <span class="select">
                    <select v-model="model.selectedSubsidiary" :disabled="!canEdit && !accountingEditing">
                      <option v-for="(so, i) in subsidiaryList" :key="'subsidiary-option-' + i">{{so}}</option>
                    </select>
                  </span>
                </div>
                <div class="control is-expanded" v-if="model.subsidiaryMode == 'New'">
                  <input class="input" type="text" placeholder="Input new subsidiary" v-model="model.newSubsidiary" :readonly="!canEdit && !accountingEditing">
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">PO#</label>
              <div class="control model-select-container">
                <input class="input" type="text" placeholder="POXXXX" v-model="model.poNsId" :readonly="!canEdit && !accountingEditing">
              </div>
            </div>

            <div class="field">
              <label class="label">Expense Account <span class="has-text-danger">*</span></label>
              <div class="control model-select-container">
                <model-select :options="accountOptions" v-model="model.expenseAccount" :isDisabled="!canEdit && !accountingEditing"/>
              </div>
            </div>

            <div class="field">
              <label class="label">Expense items <span class="has-text-danger">*</span></label>
              <div class="control">
                <my-table :itemSchema="expenseItemSchema" :items="model.items || []" :editable="canEdit || accountingEditing" @itemsChanged="itemsChanged" :showLineNum="true" :showTotals="true">
                </my-table>
              </div>
            </div>

            <div class="field">
              <label class="label">Container #</label>
              <div class="control">
                <input class="input" type="text" placeholder="Container #" v-model="model.containerNum" :readonly="!canEdit && !accountingEditing">
              </div>
            </div>

            <div class="field">
              <label class="label">Memo</label>
              <div class="control">
                <textarea class="textarea" placeholder="Memo" v-model="model.memo" :readonly="!canEdit && !accountingEditing"></textarea>
              </div>
            </div>
            
            <div class="field is-grouped mt-5" v-if="cost.status == 'Open' || (cost.status == 'Approved' && accountingEditing)">
              <div class="control" v-if="canEdit || (cost.status == 'Approved' && isAccounting)">
                <button class="button is-link" :disabled="!costChanged" :class="{'is-loading': waitings.saving}" @click="saveCost">
                  Save
                </button>
              </div>
              <div class="control">
                <button class="button is-light" :class="{'is-loading': waitings.sendingEmail}" @click="sendEmail">Send Email</button>
              </div>
            </div>
            
            <div v-if="costChanged" class="notification is-warning is-light">
              You have unsaved changes!
            </div>

            <hr/>

            <div class="field is-grouped">
              <div class="control" v-if="canClose">
                <button class="button is-success" :class="{'is-loading': waitings.approving}" :disabled="!canApprove" @click="approveCost">
                  Approve
                </button>
              </div>
              <div class="control" v-if="canSubmit">
                <button class="button is-dark" :class="{'is-loading': waitings.submitting}" @click="submitCost" :disabled="!canSubmitToNS">
                  Submit NS Bill
                </button>
              </div>
              <div class="control" v-if="canClose">
                <button class="button is-warning" :class="{'is-loading': waitings.rejecting}" @click="rejectCost">Reject</button>
              </div>
              <div class="control" v-if="canEdit">
                <button class="button is-danger" :class="{'is-loading': waitings.deleting}" @click="deleteCost">Delete</button>
              </div>
              <div class="control">
                <router-link :to="'/new-invoice/' + costId" class="button is-light">Copy to New</router-link>
              </div>
            </div>
            <p v-if="canClose && !canApprove" class="help is-danger">The mandatory fields (with *) must be filled before approval.</p>
          </div>
        </div>
      </div>

      <submit-cost-modal :opened="submitCostModal.opened" :costMessage="submitCostModal.costMessage" @closeSubmitCostModal="closeSubmitCostModal" />
    </div>
  </div>
</template>

<script>
import dateFormat from 'dateformat'
import Attachments from '@/components/attachments/Attachments'
import MyTable from '@/components/tables/MyTable'
import { ModelSelect } from 'vue-search-select'
import SubmitCostModal from '../components/modals/SubmitCostModal.vue'

function formatPrice (value) {
  if (!value) {
    return '$0.00'
  }
  return '$' + value.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    useGrouping: false
  })
}

export default {
  name: 'Cost',
  components: {
    Attachments,
    ModelSelect,
    MyTable,
    SubmitCostModal
  },
  data () {
    return {
      error: '',
      waitings: {
        loading: false,
        saving: false,
        sendingEmail: false,
        approving: false,
        rejecting: false,
        deleting: false,
      },
      cost: null,
      model: null,
      newTag: '',
      emails: [],
      accounts: [],
      locations: [],
      myClasses: [],
      modelItemsChanged: false,
      submitCostModal: {
        opened: false,
        costMessage: null,
      },
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    vendorList () {
      return this.$store.state.config.vendorList
    },
    modelVendorName () {
      if (!this.model) {
        return ''
      }
      return this.model.vendorNameMode == 'New' ? this.model.newVendorName : this.model.selectedVendorName
    },
    vendorOptions () {
      return this.vendorList.map(item => {
        return {value: item, text: item}
      })
    },
    subsidiaryList () {
      return this.$store.state.config.subsidiaryList
    },
    vendorSubsidiaryMap () {
      return this.$store.state.config.vendorSubsidiaryMap
    },
    vendorAccountNumbersMap () {
      return this.$store.state.config.vendorAccountNumbersMap
    },
    vendorExpenseAccountMap () {
      return this.$store.state.config.vendorExpenseAccountMap
    },
    token () {
      return this.$store.state.user.token
    },
    email () {
      return this.$store.state.user.email
    },
    costId () {
      return this.$route.params.costId
    },
    costChanged () {
      if (this.cost.subject != this.model.subject
          || this.cost.description != this.model.description
          || this.cost.amount != this.model.amount
          || this.cost.comment != this.model.comment
          || this.cost.reviewers != this.model.reviewers
          || this.cost.invoiceNumber != this.model.invoiceNumber
          || this.cost.invoiceDate != this.model.invoiceDate
          || this.cost.poNsId != this.model.poNsId
          || this.cost.expenseAccount != this.model.expenseAccount
          || this.cost.memo != this.model.memo
          || this.cost.containerNum != this.model.containerNum
          || this.modelItemsChanged) {
        return true
      }
      if (this.cost.tags != this.model.tags.join(',')) {
        return true
      }
      if (this.cost.attachments != this.model.attachments.join(',')) {
        return true
      }
      var vendorName = this.model.vendorNameMode == 'New' ? this.model.newVendorName : this.model.selectedVendorName
      if (this.cost.vendorName != vendorName) {
        return true
      }
      var subsidiary = this.model.subsidiaryMode == 'New' ? this.model.newSubsidiary : this.model.selectedSubsidiary
      if (this.cost.subsidiary != subsidiary) {
        return true
      }
      return false
    },
    anyWaiting () {
      for (let key in this.waitings) {
        if (this.waitings[key]) {
          return true
        }
      }
      return false
    },
    isAccounting () {
      return this.$store.state.user.groups.includes('Accounting')
    },
    accountingEditing () {
      return this.cost.status == 'Approved' && this.isAccounting
    },
    canEdit () {
      if (!this.cost) {
        return false
      }
      if (this.cost.status != 'Open') {
        return false
      }
      if (this.cost.createdBy == this.email || this.cost.reviewers.includes(this.email)) {
        return true
      }
      return false
    },
    canClose () {
      if (!this.cost) {
        return false
      }
      if (this.cost.status != 'Open') {
        return false
      }
      if (this.cost.reviewers.includes(this.email)) {
        return true
      }
      return false
    },
    canApprove () {
      if (!this.canClose) {
        return false
      }
      return true
    },
    canSubmit () {
      if (!this.cost) {
        return false
      }
      if (this.cost.status != 'Approved') {
        return false
      }
      return this.isAccounting
    },
    canSubmitToNS () {
      var message = this.modelToCost(this.model)
      return message.invoiceNumber && message.vendorName && message.subsidiary
        && message.expenseAccount && message.items && message.items.length && message.invoiceDate
    },
    canDelete () {
      if (!this.cost) {
        return false
      }
      if (this.cost.status != 'Open') {
        return false
      }
      if (this.cost.createdBy == this.email) {
        return true
      }
      return false
    },
    accountOptions () {
      return this.accounts.map(a => {
        return {value: a.nsId, text: (a.accountNumber.trim() ?  (a.accountNumber.trim() + ' : ' + a.name) : a.name)}
      })
    },
    accountNumberMap () {
      var accountNumberMap = {}
      this.accounts.forEach(a => {
        if (a.accountNumber.trim()) {
          accountNumberMap[a.accountNumber] = a.nsId
        }
      })
      return accountNumberMap
    },
    itemAccountNsId () {
      if(!this.modelVendorName) {
        return ''
      }
      var itemAccounts = this.vendorAccountNumbersMap[this.modelVendorName]
      if (!itemAccounts) {
        return ''
      }
      var accountNsId = this.accountNumberMap[itemAccounts[0]]
      if (!accountNsId) {
        return ''
      }
      return accountNsId
    },
    locationOptions () {
      var opts = this.locations.map(l => ({value: l.nsId, text: l.name}))
      opts.unshift({value: '', text: ''})
      return opts
    },
    myClassOptions () {
      var opts = this.myClasses.map(c => ({value: c.nsId, text: c.name}))
      opts.unshift({value: '', text: ''})
      return opts
    },
    expenseItemSchema () {
      return [
        {name: 'account', type: 'string', label: 'Account', options: this.accountOptions, copyPre: true, required: true, defaultValue: this.itemAccountNsId},
        {name: 'amount', type: 'number', label: 'Amount', formatter: formatPrice, total: this.model.amount, required: true},
        {name: 'location', type: 'string', label: 'Location', options: this.locationOptions, copyPre: true},
        {name: 'myClass', type: 'string', label: 'Class', options: this.myClassOptions, copyPre: true},
        {name: 'memo', type: 'string', label: 'Memo', required: true}
      ]
    },
  },
  watch: {
    costId: function (val) {
      this.getCost()
    },
    modelVendorName: function (val) {
      if (this.model.subsidiaryMode == 'Select' && this.model.selectedVendorName != this.cost.vendorName) {
        var subsidiary = this.vendorSubsidiaryMap[val]
        if (subsidiary) {
          this.model.selectedSubsidiary = subsidiary
        }
        var expenseAccount = this.vendorExpenseAccountMap[val]
        if (expenseAccount) {
          var accountNsId = this.accountNumberMap[expenseAccount]
          if (accountNsId) {
            this.model.expenseAccount = accountNsId
          }
        }
      }
    },
  },
  methods: {
    getCost () {
      this.waitings.loading = true
      this.$http.get(this.server + '/myapp/get-cost/' + this.costId + '/').then(resp => {
        this.cost = resp.body
        this.model = this.costToModel(resp.body)
        this.modelItemsChanged = false
        this.waitings.loading = false
      }, err => {
        this.error = 'Cannot find the invoice: ' + this.costId + '. It might not exist or be deleted.'
        this.waitings.loading = false
      })
    },
    costToModel (cost) {
      var model = {
        subject: cost.subject,
        description: cost.description,
        amount: cost.amount,
        createdAt: dateFormat(new Date(cost.createdAt * 1000), 'mm/dd/yyyy h:MM TT'),
        updatedAt: dateFormat(new Date(cost.updatedAt * 1000), 'mm/dd/yyyy h:MM TT'),
        createdBy: cost.createdBy,
        status: cost.status,
        closedBy: cost.closedBy || 'Not yet',
        reviewers: cost.reviewers,
        tags: cost.tags.split(',').filter(t => t),
        comment: cost.comment,
        attachments: cost.attachments.split(',').filter(a => a),
        invoiceNumber: cost.invoiceNumber,
        selectedVendorName: this.vendorList.includes(cost.vendorName) ? cost.vendorName : '',
        vendorNameMode: this.vendorList.includes(cost.vendorName) || !cost.vendorName ? 'Select' : 'New',
        newVendorName: this.vendorList.includes(cost.vendorName) ? '' : cost.vendorName,
        selectedSubsidiary: this.subsidiaryList.includes(cost.subsidiary) ? cost.subsidiary : '',
        subsidiaryMode: this.subsidiaryList.includes(cost.subsidiary) || !cost.subsidiary ? 'Select' : 'New', 
        newSubsidiary: this.subsidiaryList.includes(cost.subsidiary) ? '' : cost.subsidiary,
        expenseAccount: cost.expenseAccount ? cost.expenseAccount : '259',
        invoiceDate: cost.invoiceDate,
        poNsId: cost.poNsId,
        items: cost.items || [],
        memo: cost.memo,
        containerNum: cost.containerNum,
      }
      return model
    },
    modelToCost () {
      var cost = {
        id: this.costId,
        createdBy: this.model.createdBy,
        subject: this.model.subject,
        description: this.model.description,
        status: this.cost.status,
        closedBy: this.cost.closedBy,
        reviewers: this.model.reviewers,
        tags: this.model.tags.join(','),
        amount: this.model.amount,
        comment: this.model.comment,
        attachments: this.model.attachments.join(','),
        invoiceNumber: this.model.invoiceNumber,
        vendorName: this.model.vendorNameMode == 'New' ? this.model.newVendorName : this.model.selectedVendorName,
        subsidiary: this.model.subsidiaryMode == 'New' ? this.model.newSubsidiary : this.model.selectedSubsidiary,
        expenseAccount: this.model.expenseAccount,
        items: this.model.items || [],
        memo: this.model.memo,
        containerNum: this.model.containerNum,
        invoiceDate: this.model.invoiceDate,
        poNsId: this.model.poNsId,
      }
      return cost
    },
    removeTag (i) {
      this.model.tags.splice(i, 1)
    },
    addTag () {
      var newTag = this.newTag.trim()
      if (newTag && !this.model.tags.includes(newTag)) {
        this.model.tags.push(newTag)
      } 
    },
    saveCost () {
      if (this.anyWaiting || !this.costChanged) {
        return
      }
      this.waitings.saving = true
      var message = this.modelToCost(this.model)
      this.$http.post(this.server + '/myapp/update-cost/' + this.costId + '/', message).then(resp => {
        this.getCost()
        this.waitings.saving = false
      }, err => {
        this.error = err.body
        this.waitings.saving = false
      })
    },
    approveCost () {
      if (!this.canApprove) {
        return
      }
      var confirm = {
        title: 'Approve Invoice',
        message: 'Are you sure to approve this invoice?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.approveCostConfirmed,
          args: []
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    approveCostConfirmed () {
      if (this.anyWaiting || this.cost.status != 'Open') {
        return
      }
      this.waitings.approving = true
      var message = this.modelToCost(this.model)
      message.status = 'Approved'
      message.closedBy = this.email
      this.$http.post(this.server + '/myapp/update-cost/' + this.costId + '/', message).then(resp => {
        this.getCost()
        this.waitings.approving = false
      }, err => {
        this.error = err.body
        this.waitings.approving = false
      })
    },
    submitCost () {
      if (this.anyWaiting || !this.canSubmit || !this.canSubmitToNS) {
        return
      }
      var costMessage = this.modelToCost(this.model)
      costMessage.status = 'NS Bill Created'
      this.submitCostModal.costMessage = costMessage
      this.submitCostModal.opened = true
    },
    closeSubmitCostModal (result) {
      this.submitCostModal.opened = false
      if (result) {
        this.getCost()
      }
    },
    submitCostConfirmed () {
      if (this.anyWaiting || !this.canSubmit) {
        return
      }
      this.waitings.submitting = true
      var message = this.modelToCost(this.model)
      message.status = 'NS Bill Created'
      this.$http.post(this.server + '/myapp/update-cost/' + this.costId + '/', message).then(resp => {
        this.getCost()
        this.waitings.submitting = false
      }, err => {
        this.error = err.body
        this.waitings.submitting = false
      })
    },
    rejectCost () {
      var confirm = {
        title: 'Reject Invoice',
        message: 'Are you sure to reject this invoice?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.rejectCostConfirmed,
          args: []
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    rejectCostConfirmed () {
      if (this.anyWaiting || this.cost.status != 'Open') {
        return
      }
      this.waitings.rejecting = true
      var message = this.modelToCost(this.model)
      message.status = 'Rejected'
      message.closedBy = this.email
      this.$http.post(this.server + '/myapp/update-cost/' + this.costId + '/', message).then(resp => {
        this.getCost()
        this.waitings.approving = false
      }, err => {
        this.error = err.body
        this.waitings.approving = false
      })
    },
    deleteCost () {
      var confirm = {
        title: 'Delete Invoice',
        message: 'Are you sure to delete this invoice? You CANNOT restore it after the deletion.',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.deleteCostConfirmed,
          args: []
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    deleteCostConfirmed () {
      if (this.anyWaiting) {
        return
      }
      this.waitings.deleting = true
      this.$http.post(this.server + '/myapp/delete-cost/' + this.costId + '/').then(resp => {
        this.$router.push('/invoices')
        this.waitings.deleting = false
      }, err => {
        this.error = err.body
        this.waitings.deleting = false
      })
    },
    getEmails () {
      this.$http.get(this.server + '/myapp/get-all-emails/').then(resp => {
        this.emails = resp.body
      }, err => {
        this.error = err.body
      })
    },
    addAttachment (attachment) {
      this.model.attachments.push(attachment.id)
      if (attachment.invoiceData) {
        var invoiceData = attachment.invoiceData
        if (invoiceData['Subject']) {
          this.model.subject = invoiceData['Subject']
        }
        if (invoiceData['Invoice Amount']) {
          this.model.amount = invoiceData['Invoice Amount']
        }
        if (invoiceData['Invoice Date']) {
          this.model.invoiceDate = invoiceData['Invoice Date']
        }
        if (invoiceData['Invoice Number']) {
          this.model.invoiceNumber = invoiceData['Invoice Number']
        }
        if (this.model.comment.trim()) {
          this.model.comment += ('\n' + JSON.stringify(invoiceData))
        } else {
          this.model.comment = JSON.stringify(invoiceData)
        }
      }
    },
    removeAttachment (attachmentId) {
      var index = this.model.attachments.indexOf(attachmentId)
      if (index > -1) {
        this.model.attachments.splice(index, 1)
      }
    },
    sendEmail () {
      if (this.anyWaiting) {
        return
      }
      this.waitings.sendingEmail = true
      this.$http.post(this.server + '/myapp/send-email-for-cost/' + this.costId + '/').then(resp => {
        this.waitings.sendingEmail = false
      }, err => {
        this.error = err.body
        this.waitings.sendingEmail = false
      })
    },
    getAccounts () {
      this.$http.get(this.server + '/myapp/get-accounts/').then(resp => {
        this.accounts = resp.body
      }, err => {
        this.error = err.body
      })
    },
    getLocations () {
      this.$http.get(this.server + '/myapp/get-locations/').then(resp => {
        this.locations = resp.body
      }, err => {
        this.error = err.body
      })
    },
    getMyClasses () {
      this.$http.get(this.server + '/myapp/get-my-classes/').then(resp => {
        this.myClasses = resp.body
      }, err => {
        this.error = err.body
      })
    },
    itemsChanged (val) {
      if (this.model.items.length != val.length) {
        this.modelItemsChanged = true
        this.model.items = val
      } else {
        for (var i = 0; i < this.model.items.length; i++) {
          for (var f of this.expenseItemSchema) {
            if (this.model.items[i][f.name] != val[i][f.name]) {
              this.modelItemsChanged = true
              this.model.items = val
              return
            }
          }
        }
      }
    },
  },
  mounted () {
    this.$nextTick(function () {
      this.getCost()
      this.getEmails()
      this.getAccounts()
      this.getLocations()
      this.getMyClasses()
    })
  },
}
</script>

<style scoped lang="scss">
 .model-select-container {
   .ui {
     height: 100%;
   }
 }
</style>
